import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Loader2 } from 'lucide-react'
import type { Contacto, ContactoUpdateInput } from '@/modules/bd-unificada/types/contacto'
import { GENEROS, TIPOS_CONTACTO } from '@/modules/bd-unificada/types/contacto'

const schema = z.object({
  nombre_completo: z.string().min(1, 'El nombre completo es requerido'),
  nombre: z.string().optional(),
  apellido: z.string().optional(),
  tipo: z.string().optional(),
  cedula: z.string().optional(),
  especialidad: z.string().optional(),
  email_personal: z.string().email('Email inválido').optional().or(z.literal('')),
  email_corporativo: z.string().email('Email inválido').optional().or(z.literal('')),
  telefono: z.string().optional(),
  whatsapp: z.string().optional(),
  pais: z.string().optional(),
  ciudad: z.string().optional(),
  provincia: z.string().optional(),
  genero: z.string().optional(),
})

type FormData = z.infer<typeof schema>

interface ContactoFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ContactoUpdateInput) => Promise<any> | void
  isSubmitting: boolean
  contacto?: Contacto | null
}

export function ContactoFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  contacto,
}: ContactoFormDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {},
  })

  useEffect(() => {
    if (contacto) {
      reset({
        nombre_completo: contacto.nombre_completo ?? '',
        nombre: contacto.nombre ?? '',
        apellido: contacto.apellido ?? '',
        tipo: contacto.tipo ?? '',
        cedula: contacto.cedula ?? '',
        especialidad: contacto.especialidad ?? '',
        email_personal: contacto.email_personal ?? '',
        email_corporativo: contacto.email_corporativo ?? '',
        telefono: contacto.telefono ?? '',
        whatsapp: contacto.whatsapp ?? '',
        pais: contacto.pais ?? '',
        ciudad: contacto.ciudad ?? '',
        provincia: contacto.provincia ?? '',
        genero: (contacto.genero as string) ?? '',
      })
    }
  }, [contacto, reset])

  const submit = (data: FormData) => {
    // Enviar solo campos con valor (el backend hace exclude_unset en el proxy).
    const payload: ContactoUpdateInput = {}
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== '') (payload as any)[k] = v
    })
    return onSubmit(payload)
  }

  const tipo = watch('tipo')
  const genero = watch('genero')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar contacto</DialogTitle>
          <DialogDescription>
            Actualiza los datos del contacto. Solo los campos modificados se enviarán.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-1">
              <Label htmlFor="nombre_completo">Nombre completo</Label>
              <Input id="nombre_completo" {...register('nombre_completo')} />
              {errors.nombre_completo && (
                <p className="text-xs text-destructive">{errors.nombre_completo.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="nombre">Nombre</Label>
              <Input id="nombre" {...register('nombre')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="apellido">Apellido</Label>
              <Input id="apellido" {...register('apellido')} />
            </div>

            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={tipo || ''} onValueChange={(v) => setValue('tipo', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CONTACTO.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cedula">Cédula</Label>
              <Input id="cedula" {...register('cedula')} />
            </div>

            <div className="space-y-1">
              <Label htmlFor="especialidad">Especialidad</Label>
              <Input id="especialidad" {...register('especialidad')} />
            </div>
            <div className="space-y-1">
              <Label>Género</Label>
              <Select value={genero || ''} onValueChange={(v) => setValue('genero', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Género" />
                </SelectTrigger>
                <SelectContent>
                  {GENEROS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email_personal">Email personal</Label>
              <Input id="email_personal" {...register('email_personal')} />
              {errors.email_personal && (
                <p className="text-xs text-destructive">{errors.email_personal.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="email_corporativo">Email corporativo</Label>
              <Input id="email_corporativo" {...register('email_corporativo')} />
              {errors.email_corporativo && (
                <p className="text-xs text-destructive">{errors.email_corporativo.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="telefono">Teléfono</Label>
              <Input id="telefono" {...register('telefono')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="whatsapp">WhatsApp</Label>
              <Input id="whatsapp" {...register('whatsapp')} />
            </div>

            <div className="space-y-1">
              <Label htmlFor="pais">País</Label>
              <Input id="pais" {...register('pais')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ciudad">Ciudad</Label>
              <Input id="ciudad" {...register('ciudad')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="provincia">Provincia</Label>
              <Input id="provincia" {...register('provincia')} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-blue-800 hover:bg-blue-900 text-white">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar cambios
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
